import { useStore } from '@contexts/store'
import { APIResponseDataType } from '@framework/model-types/APIResponseDataType'
import { APIReturnType } from '@framework/model-types/APIReturnType'
import { Product, ProductsAPIData } from '@framework/model-types/Product'
import { useApi, useApiInfinite, usePreloader } from '@utils/api'
import { API_ENDPOINTS } from '@utils/api-endpoints'
import { isEmpty } from 'lodash'

export const useGetShopProducts = (
  shopId: string,
  filter?: Record<string, any> | string,
  options?: any,
): {
  products: ProductsAPIData
  isLoading: boolean
  isError: ReturnType<typeof useApi>['isError']
} => {
  const { storeData } = useStore()
  const inventory =
    storeData?.config?.product?.inventory?.storefrontDisplaySoldOut
  let queryString = typeof filter === 'string' ? filter : ''

  if (filter && typeof filter === 'object') {
    queryString = Object.keys(filter)
      .map((key) => {
        return encodeURIComponent(key) + '=' + encodeURIComponent(filter[key])
      })
      .join('&')
  }

  if (!inventory) {
    queryString += '&stock>0'
  }
  const endpoint = API_ENDPOINTS.GET_SHOP_PRODUCTS(shopId)
  const url = filter ? `${endpoint}?${queryString}` : endpoint
  const { data, isError, isLoading } = useApi(shopId ? url : null, options)
  return {
    products: data?.data,
    isLoading,
    isError,
  }
}

export const useGetShopProductsPage = (shopId: string, filter?: any) => {
  // console.log('filter', filter)
  const { storeData } = useStore()
  const inventory =
    storeData?.config?.product?.inventory?.storefrontDisplaySoldOut

  let queryString = ''

  if (filter) {
    queryString = Object.keys(filter)
      .map((key) => {
        return encodeURIComponent(key) + '=' + encodeURIComponent(filter[key])
      })
      .join('&')
  }
  if (!inventory) {
    queryString += '&stock>0'
  }
  const endpoint: string = API_ENDPOINTS.GET_SHOP_PRODUCTS(shopId)
  const url: string = filter ? `${endpoint}?${queryString}` : endpoint
  const { data, isError, isLoading } = useApi(url)
  return {
    products: data,
    isLoading,
    isError,
  }
}

interface ProductAPIReturnType extends APIReturnType {
  product: Product
  notFound: boolean
}

export const useGetShopProductBySlugOrID = (
  shopId: string,
  slug: any,
  options?: any,
): ProductAPIReturnType => {
  const url: string = `${API_ENDPOINTS.GET_SINGLE_PRODUCT(shopId, slug)}`
  const { data, isError, isLoading, mutate, key } = useApi(
    slug ? url : '',
    options,
  )
  return {
    product: data?.data,
    isLoading: isLoading || (!isError && !data),
    notFound: !isLoading && !isError && data && isEmpty(data?.data),
    isError,
    mutate,
    key,
  }
}

export const usePreloadProductSlug = (slug: string) => {
  const { storeData } = useStore()
  const url: string = `${API_ENDPOINTS.GET_SINGLE_PRODUCT(
    storeData?._id,
    slug,
  )}`
  const preload = usePreloader(url)

  return preload
}

export const useGetRelatedShopProducts = (
  shopId: string,
  product: any,
  limit: number | string = 10,
) => {
  const { storeData } = useStore()
  const inventory =
    storeData?.config?.product?.inventory?.storefrontDisplaySoldOut

  let queryString = ''
  const tagsIDs = product?.tags?.map((tag: any) => tag?._id) ?? []
  const tagSlugs = product?.tags?.map((tag: any) => tag?.slug) ?? []
  if (tagsIDs?.length) {
    queryString += `tags=${tagsIDs.join(',')}`
  }
  if (!inventory) {
    queryString += `&stock>0`
  }
  const filter = `category=${tagSlugs.join(',')}`
  const url: string = `${API_ENDPOINTS.GET_SHOP_PRODUCTS(
    shopId,
  )}?limit=${limit}${queryString?.length ? '&' + queryString : ''}`

  const { data, isError, isLoading } = useApi(!isEmpty(tagsIDs) ? url : '')
  return {
    relatedProducts: data?.data,
    isLoading,
    isError,
    filter: filter,
  }
}

export const useGetRelatedShopProductsByFilter = (
  shopId: string,
  filter: Array<{
    id: string
    slug: string
  }>,
  limit: number | string = 10,
) => {
  const { storeData } = useStore()
  const inventory =
    storeData?.config?.product?.inventory?.storefrontDisplaySoldOut

  let queryString = ''
  const tagsIDs = filter?.map((tag: any) => tag?.id) ?? []
  const tagSlugs = filter?.map((tag: any) => tag?.slug) ?? []
  if (tagsIDs?.length) {
    queryString += `tags=${tagsIDs.join(',')}`
  }
  if (!inventory) {
    queryString += `&stock>0`
  }
  const filter_ = `category=${tagSlugs.join(',')}`
  const url: string = `${API_ENDPOINTS.GET_SHOP_PRODUCTS(
    shopId,
  )}?limit=${limit}${queryString?.length ? '&' + queryString : ''}`
  const { data, isError, isLoading } = useApi(url)
  return {
    relatedProducts: data?.data,
    isLoading,
    isError,
    filter: filter_,
  }
}

export function useInfiniteProducts(
  shopId?: string,
  filter?: string,
  page_size = 15,
) {
  const endpoint = shopId ? API_ENDPOINTS.GET_SHOP_PRODUCTS(shopId) : null

  const url = endpoint ? `${endpoint}${filter ? `?${filter}` : ''}` : null

  const { data, size, setSize, isLoading, isValidating, error } =
    useApiInfinite<APIResponseDataType<ProductsAPIData>>(url)

  return {
    data,
    size,
    setSize,
    isLoading,
    isValidating,
    error,
  }
}
